import {AggregatedDataPoint, DataType, Location, MeasurementsResult} from '@flowmaps-com/flowmaps-typescriptmodels';
import type {Entity} from '../../handlers/entity';
import {isMeterVisible} from '../refdata/meters/submeter-role.utils';

export interface MeterChartSource {
    id: string;
    name?: string;
    source?: Entity;
    type: unknown;
}

export interface MeterChartLookups {
    liveMeterEntities: Map<string, Entity>;
    meterSources: Map<string, MeterChartSource>;
    selectedSourceNames: Map<string, string>;
    parentMeterDataById: Map<string, boolean>;
    entitiesByDataType: Map<DataType, string[]>;
    entityIndexByDataType: Map<DataType, Map<string, number>>;
}

export function buildMeterChartLookups(
    data: MeasurementsResult[],
    liveLocations: Map<string, Location>,
    allSelectedSources: MeterChartSource[],
    cleanedSources: MeterChartSource[],
    meterEntityType: unknown
): MeterChartLookups {
    const liveMeterEntities = new Map<string, Entity>();
    for (const location of liveLocations.values()) {
        for (const connection of location.connections || []) {
            for (const meter of (connection.meters || []).filter(isMeterVisible)) {
                if (!liveMeterEntities.has(meter.meterId)) {
                    liveMeterEntities.set(meter.meterId, {location, connection, meter} as Entity);
                }
            }
        }
    }

    const meterSources = new Map<string, MeterChartSource>();
    for (const source of [...allSelectedSources, ...cleanedSources]) {
        if (source.type === meterEntityType && !meterSources.has(source.id)) {
            meterSources.set(source.id, source);
        }
    }

    const selectedSourceNames = new Map<string, string>();
    for (const source of allSelectedSources) {
        if (!selectedSourceNames.has(source.id)) {
            selectedSourceNames.set(source.id, source.name);
        }
    }

    const selectedSourceIds = new Set(cleanedSources.map(source => source.id));
    const parentMeterDataById = new Map<string, boolean>();
    for (const source of cleanedSources) {
        if (source.type !== meterEntityType || parentMeterDataById.has(source.id)) continue;
        const childIds = source.source?.getChildIds?.();
        if (childIds?.length) {
            parentMeterDataById.set(source.id, childIds.every(id => selectedSourceIds.has(id)));
        }
    }

    const entitiesByDataType = new Map<DataType, string[]>();
    for (const result of data) {
        for (const [type, points] of Object.entries(result.measurements || {})) {
            if ((points as AggregatedDataPoint[]).some(point => point.value !== 0)) {
                const dataType = type as DataType;
                const entities = entitiesByDataType.get(dataType) || [];
                entities.push(result.entityId);
                entitiesByDataType.set(dataType, entities);
            }
        }
    }

    const entityIndexByDataType = new Map<DataType, Map<string, number>>();
    for (const [type, entities] of entitiesByDataType) {
        const indexes = new Map<string, number>();
        entities.forEach((entityId, index) => {
            if (!indexes.has(entityId)) indexes.set(entityId, index);
        });
        entityIndexByDataType.set(type, indexes);
    }

    return {
        liveMeterEntities,
        meterSources,
        selectedSourceNames,
        parentMeterDataById,
        entitiesByDataType,
        entityIndexByDataType
    };
}
